import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { chargeDateKey, chargesForMonth, formatMonthKey } from "../utils/chargeDates";
import { getIncomePeriods } from "../../utils/incomePeriods";
import {
  getIncomePeriodRatePolicy,
  monthKeyFromDate,
} from "../utils/incomePeriod";
import { monthlyFeeForIncomePeriod } from "../../../Adviser/Tabs/Rates/rateHistory";
import { subscribeAMLChargeRecords, loadAMLArchiveOnce } from "../../AMLStatements.js/amlDashboard";
import { subscribeParaplanningCases } from "../../../Paraplanning/services/paraplanningCasesService";
import { subscribeProCedingChargeRecords } from "../services/proCedingChargeService";
import { subscribeAdviserRateRows } from "../../../Adviser/Tabs/Rates/ratesService";
import { subscribeWebsites } from "../../../Marketing/MarketingDashabord/services/websiteService";
import { subscribePrintingCharges } from "../../../Marketing/MarketingDashabord/services/printingChargeService";
import { subscribeEmailLicences } from "../../../Marketing/MarketingDashabord/services/emailLicenceService";
import { subscribeMonthlyManualCharges } from "../services/discountChargeService";
import { getMonthlyStatementConfiguration } from "../services/monthlyStatementArchiveService";
import { applyAmlPaymentExceptions } from "../utils/amlPaymentExceptions";
import { findMonthlyFeeGuideline, isMonthlyFeeApplicable } from "../utils/monthlyFeeGuideline";
import { resolveMonthlyFeePayer } from "../utils/monthlyFeePayer";
import { isChargeMonthClosed } from "../utils/closedChargeMonths";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import {
  isAmlGuidelineCharge,
  MONTHLY_FEES,
  WEBSITE_CHARGES,
  TECHNOLOGY_CHARGES,
} from "../constants/guidelineCharges";
const getExpectedMonthlyFee = (row, selectedMonth) => {
  const selectedPermission = String(row.monthlyFeePermission || "")
    .trim()
    .toLowerCase();
  const guideline =
    MONTHLY_FEES.find(
      (fee) =>
        String(fee.permission || "").trim().toLowerCase() ===
          selectedPermission ||
        String(fee.id || "").trim().toLowerCase() === selectedPermission,
    ) || findMonthlyFeeGuideline(row.status);
  const ownAmount = guideline?.amount ?? Number(row.monthlyFees);
  return {
    amount: isMonthlyFeeApplicable(row, selectedMonth) ? ownAmount : NaN,
    permission: guideline?.permission || "Monthly Fees",
  };
};

const normaliseEmail = (email) => String(email || "").trim().toLowerCase();

const flagUnlinkedMarketingEmail = (record, adviserEmails) => {
  const source = record.id?.startsWith("marketing-printing-")
    ? "Printing charge"
    : record.id?.startsWith("marketing-email-")
      ? "Email licence"
      : "";
  if (!source || adviserEmails.has(normaliseEmail(record.adviserEmail))) {
    return record;
  }
  return {
    ...record,
    adviserEmailUnlinked: true,
    flagReason: `${source} has no linked adviser email`,
  };
};

const getSummaryMonths = (today = new Date()) =>
  [2, 1, 0].map((offset) => {
    const date = new Date(today.getFullYear(), today.getMonth() - offset, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    return {
      value,
      label: formatMonthKey(value),
    };
  });

const isArchivedAMLMonth = (month) => {
  const today = new Date();
  const selected = new Date(`${month}-01T00:00:00`);
  const liveCutoff = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return !Number.isNaN(selected.getTime()) && selected < liveCutoff;
};


const AdviserChargesContext = createContext(null);
export const useAdviserCharges = () => {
  const value = useContext(AdviserChargesContext);
  if (!value) throw new Error("useAdviserCharges requires AdviserChargesProvider");
  return value;
};
export const AdviserChargesProvider = ({ children }) => {
  const [monthOptions] = useState(getSummaryMonths);
  // Values and labels both identify the internal accounting month.
  const currentMonth = monthOptions[2].value;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [sources, setSources] = useState({});
  const [sourceErrors, setSourceErrors] = useState({});
  const [incomePeriods, setIncomePeriods] = useState(null);
  const [selectedIncomePeriod, setSelectedIncomePeriod] = useState(null);
  const [incomePeriodsError, setIncomePeriodsError] = useState(null);
  const [amlPaymentExceptionEmails, setAmlPaymentExceptionEmails] = useState([]);
  const [statementConfigurationLoading, setStatementConfigurationLoading] = useState(true);
  const [statementConfigurationError, setStatementConfigurationError] = useState("");
  useEffect(() => {
    let active = true;
    getMonthlyStatementConfiguration().then(
      (configuration) => {
        if (!active) return;
        setAmlPaymentExceptionEmails(
          Array.isArray(configuration.amlPaymentExceptionEmails)
            ? configuration.amlPaymentExceptionEmails
            : [],
        );
        setStatementConfigurationError("");
        setStatementConfigurationLoading(false);
      },
      () => {
        if (!active) return;
        setAmlPaymentExceptionEmails([]);
        setStatementConfigurationError(
          "Statement configuration could not be loaded.",
        );
        setStatementConfigurationLoading(false);
      },
    );
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    getIncomePeriods().then((periods) => {
      if (active) {
        setIncomePeriods(periods);
        const currentIncomeMonth = monthKeyFromDate(periods[0]?.endDatetime);
        if (currentIncomeMonth) setSelectedMonth(currentIncomeMonth);
      }
    }).catch((error) => {
      if (active) setIncomePeriodsError(error.message);
    });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    const receive = (name) => (records) => {
      if (!active) return;
      setSources((previous) => ({ ...previous, [name]: records }));
      setSourceErrors((previous) => ({ ...previous, [name]: null }));
    };
    const fail = (name) => () => {
      if (active) setSourceErrors((previous) => ({ ...previous, [name]: `${name} could not be loaded.` }));
    };
    const stops = [
      subscribeParaplanningCases({ filters: { status: "completed" }, onData: receive("Paraplanning"), onError: fail("Paraplanning") }),
      subscribeProCedingChargeRecords(receive("ProCeding"), fail("ProCeding")),
      subscribeAdviserRateRows(receive("Monthly Fees"), fail("Monthly Fees")),
      subscribeMonthlyManualCharges(receive("manualCharges"), fail("manualCharges")),
    ];
    // Archived AML workbooks are fetched lazily only when their month is selected.
    const mergeMarketing = (source, records) =>
      receive(source)(records);
    const unsubscribeWebsites = subscribeWebsites(
      (websites) =>
        mergeMarketing(
          "websites",
          websites.flatMap((website) =>
            (website.domains || []).flatMap((domain, index) => {
              const entries = [];
              const designAmount = Number(domain.design?.amountToCharge);
              const designInstallments = Math.max(1, Number(domain.design?.chargeAcross) || 1);
              if (Number.isFinite(designAmount) && chargeDateKey(domain.design?.liveDate))
                entries.push({
                  id: `marketing-website-design-${website.id}-${index}`,
                  adviser: website.adviserName,
                  adviserEmail: website.adviserEmail,
                  chargeType: "Website & Graphics",
                  description: "Website Design",
                  date: domain.design?.liveDate || website.createdAt,
                  amountCharged: designAmount / designInstallments,
                  expectedAmount: WEBSITE_CHARGES.find((charge) => charge.id === "design").amount / designInstallments,
                  fromDate: domain.design?.liveDate || website.createdAt,
                  installments: designInstallments,
                  recurrence: designInstallments > 1 ? "installment" : "one_time",
                  sourcePath: `/department/marketing/design⁴/${website.id}`,
                });
              const hostingAmount = Number(domain.hosting?.monthlyCharge);
              const hostingLiveDate = chargeDateKey(domain.design?.liveDate);
              if (Number.isFinite(hostingAmount) && hostingLiveDate)
                entries.push({
                  id: `marketing-website-hosting-${website.id}-${index}`,
                  adviser: website.adviserName,
                  adviserEmail: website.adviserEmail,
                  chargeType: "Website & Graphics",
                  description: "Website Hosting",
                  date: domain.design?.liveDate || website.createdAt,
                  fromDate: domain.design?.liveDate,
                  recurrence: "monthly",
                  amountCharged: hostingAmount,
                  expectedAmount: WEBSITE_CHARGES.find((charge) => charge.id === "hosting").amount,
                  sourcePath: `/department/marketing/design⁴/${website.id}`,
                });
              const paid = Number(domain.amountPaid);
              const markup = Number(domain.markup || 0);
              const domainAmount = Number.isFinite(paid)
                ? paid * (1 + (Number.isFinite(markup) ? markup : 0) / 100)
                : NaN;
              if (Number.isFinite(domainAmount))
                entries.push({
                  id: `marketing-domain-${website.id}-${index}`,
                  adviser: website.adviserName,
                  adviserEmail: website.adviserEmail,
                  chargeType: "Website & Graphics",
                  description: `Domain ${domain.registrationType || "Registration"}`,
                  date: domain.datePaid || website.createdAt,
                  amountCharged: domainAmount,
                  expectedAmount: domainAmount,
                  recurrence: "annual",
                  fromDate: domain.datePaid || website.createdAt,
                  sourcePath: `/department/marketing/design⁴/${website.id}`,
                });
              return entries;
            }),
          ),
        ),
      fail("websites"),
    );
    const unsubscribePrinting = subscribePrintingCharges(
      (charges) =>
        mergeMarketing(
          "printing",
          charges.map((charge) => {
            const totalAmount = Number(charge.amountToCharge || 0);
            const installments = Math.max(1, Number(charge.chargeAcross) || 1);
            const monthlyAmount = totalAmount / installments;
            return {
              id: `marketing-printing-${charge.id}`,
              adviser: charge.adviserName,
              adviserEmail: charge.adviserEmail,
              chargeType: "Website & Graphics",
              description: charge.orderDescription || "Printing",
              date: charge.orderDate || charge.createdAt,
              amountCharged: monthlyAmount,
              expectedAmount: monthlyAmount,
              recurrence: installments > 1 ? "installment" : "one_time",
              installments,
              sourcePath: `/department/marketing/printing/${charge.id}`,
            };
          }),
        ),
      fail("printing"),
    );
    const unsubscribeEmail = subscribeEmailLicences(
      (licences) =>
        mergeMarketing(
          "email",
          licences.map((licence) => ({
            id: `marketing-email-${licence.id}`,
            adviser: licence.adviserName,
            adviserEmail: licence.adviserEmail,
            chargeType: "Technology",
            description: licence.licenceType || "Email Licence",
            date: licence.chargeFromDate || licence.orderDate || licence.createdAt,
            amountCharged: Number(licence.monthlyAmount || 0),
            expectedAmount: (String(licence.licenceType || "").includes("365")
              ? TECHNOLOGY_CHARGES.find((item) => item.id === "email-365")?.amount ?? 30
              : TECHNOLOGY_CHARGES.find((item) => item.id === "email")?.amount ?? 15) *
              Math.max(1, Number(licence.numberOfLicences) || 1),
            fromDate: licence.chargeFromDate || licence.orderDate || licence.createdAt,
            recurrence: "monthly",
            sourcePath: `/department/marketing/email-licences/${licence.id}`,
          })),
        ),
      fail("email"),
    );
    return () => {
      active = false;
      stops.forEach((stop) => stop());
      unsubscribeWebsites();
      unsubscribePrinting();
      unsubscribeEmail();
    };
  }, []);
  useEffect(() => {
    let active = true;
    setSources((previous) => ({
      ...previous,
      AML: undefined,
      "AML archive": undefined,
      "AML month": undefined,
    }));
    setSourceErrors((previous) => ({
      ...previous,
      AML: null,
      "AML archive": null,
    }));

    if (isArchivedAMLMonth(selectedMonth)) {
      loadAMLArchiveOnce(selectedMonth).then(
        (records) => {
          if (!active) return;
          setSources((previous) => ({
            ...previous,
            AML: [],
            "AML archive": records,
            "AML month": selectedMonth,
          }));
        },
        () => {
          if (!active) return;
          setSources((previous) => ({
            ...previous,
            AML: [],
            "AML archive": [],
            "AML month": selectedMonth,
          }));
          setSourceErrors((previous) => ({
            ...previous,
            "AML archive": "AML archive could not be loaded.",
          }));
        },
      );
      return () => { active = false; };
    }

    setSources((previous) => ({ ...previous, "AML archive": [] }));
    const unsubscribe = subscribeAMLChargeRecords(
      selectedMonth,
      (records) => {
        if (active) setSources((previous) => ({
          ...previous,
          AML: records,
          "AML month": selectedMonth,
        }));
      },
      () => {
        if (!active) return;
        setSources((previous) => ({
          ...previous,
          AML: [],
          "AML month": selectedMonth,
        }));
        setSourceErrors((previous) => ({
          ...previous,
          AML: "AML could not be loaded.",
        }));
      },
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, [selectedMonth]);

  const selectedRatePolicy = useMemo(
    () => getIncomePeriodRatePolicy(
      incomePeriods || [],
      selectedMonth,
      currentMonth,
    ),
    [incomePeriods, selectedMonth, currentMonth],
  );
  const rateRows = useMemo(() => (sources["Monthly Fees"] || []).map((row) => ({
    ...row,
    monthlyFees: monthlyFeeForIncomePeriod(
      row.rateData,
      row.history,
      selectedMonth,
      selectedRatePolicy,
    ),
  })), [
    sources,
    selectedMonth,
    selectedRatePolicy,
  ]);
  const requiredSources = ["AML", "Paraplanning", "ProCeding", "Monthly Fees", "websites", "printing", "email", "manualCharges",
    ...(isArchivedAMLMonth(selectedMonth) ? ["AML archive"] : [])];
  const chargesLoading = statementConfigurationLoading ||
    sources["AML month"] !== selectedMonth ||
    requiredSources.some((name) => sources[name] === undefined && !sourceErrors[name]);
  const chargeErrors = requiredSources.map((name) => sourceErrors[name]).filter(Boolean);
  if (statementConfigurationError) chargeErrors.push(statementConfigurationError);
  if (rateRows.some((row) => row.monthlyFees == null && isMonthlyFeeApplicable(row, selectedMonth))) chargeErrors.push("");
  const loadedChargeRecords = useMemo(() => {
    const adviserEmails = new Set(
      rateRows.map((row) => normaliseEmail(row.adviserEmail)).filter(Boolean),
    );
    const amlRecords = [...(sources.AML || []), ...(sources["AML archive"] || [])];
      const paraplanningRecords = true
        ? (sources.Paraplanning || []).map((item) => {
            const dateValue =
              item.completionDate || item.startDate || item.createdAt;
            const date = dateValue?.toDate?.() || new Date(dateValue);
            const amountCharged = Number(item.costs?.totalCost || 0);
              const baseAmount = Number(
                item.costs?.baseCost ?? item.chargeSnapshot?.baseAmount ?? 0,
              );
              const additionalAmount = Number(
                item.costs?.additionalCost ??
                  item.chargeSnapshot?.additionalCost ??
                  0,
              );
              const modifiers = Array.isArray(item.chargeSnapshot?.modifiers)
                ? item.chargeSnapshot.modifiers
                : Array.isArray(item.costs?.modifiers)
                  ? item.costs.modifiers
                  : [];
              const hasCustomOrDiscount = modifiers.some(
                (modifier) => modifier?.isCustom === true || modifier?.isDiscount === true,
              );
              const caseType = item.type || item.chargeSnapshot?.itemName || "Case";
              const flagReasons = modifiers
                .map((modifier) => {
                  if (modifier?.isDiscount === true) return "Discount";
                  if (modifier?.isCustom === true) {
                    return modifier.label || modifier.name || "Custom Charge";
                  }
                  return modifier.label || modifier.name || "";
                })
                .filter(Boolean);
              const chargeDescription = [caseType, ...flagReasons]
                .filter((value, index, values) => values.indexOf(value) === index)
                .join(" – ");
            return {
              id: `paraplanning-${item.id}`,
              adviserEmail: item.adviser?.email,
              adviser: item.adviser?.name || item.adviser?.email || "",
              client:
                (Array.isArray(item.clients) ? item.clients : [])
                  .map((client) => client?.name || [client?.firstName, client?.lastName].filter(Boolean).join(" "))
                  .filter(Boolean)
                  .join("; ") || item.client?.name || "",
              paraplanner:
                item.assignedTo?.name || item.assignedTo?.email || "",
              chargeType: "Paraplanning",
              description: chargeDescription,
              date: Number.isNaN(date.getTime())
                ? `${selectedMonth}-01`
                : date.toISOString(),
              amountCharged,
                expectedAmount: baseAmount + additionalAmount,
                forceFlag: hasCustomOrDiscount,
              sourcePath: `/paraplanning?caseId=${encodeURIComponent(item.id)}`,
            };
          })
        : [];
      const proCedingRecords = sources.ProCeding || [];
      const rateRecords = true
        ? rateRows.flatMap((row) => {
            if (
              row.monthlyFees === null ||
              row.monthlyFees === undefined ||
              row.monthlyFees === ""
            )
              return [];
            const actual = Number(row.monthlyFees);
            const expectedData = getExpectedMonthlyFee(row, selectedMonth);
            const expected = Number(expectedData.amount);
            if (!Number.isFinite(actual) || !Number.isFinite(expected))
              return [];
            const payer = resolveMonthlyFeePayer(row, rateRows);
            return [
              {
                id: `ifa-rate-${row.adviserId}-${selectedMonth}`,
                adviser: payer.adviser,
                adviserEmail: payer.adviserEmail,
                practice: payer.practice,
                chargedForAdviser: payer.chargedForAdviser,
                chargedForAdviserEmail: payer.chargedForAdviserEmail,
                paidBy: row.paidBy,
                chargeType: "Monthly Fees",
                amountLabel: "Monthly Charge",
                description: `IFA Rates (${expectedData.permission})${payer.paidByAnotherAdviser ? ` – for ${payer.chargedForAdviser}` : ""}`,
                date: `${selectedMonth}-01`,
                amountCharged: actual,
                expectedAmount: expected,
                sourcePath: `/advisers/${encodeURIComponent(row.adviserEmail || row.adviserId)}/rates/edit`,
              },
            ];
          })
        : [];

    const manualChargeRecords = (sources.manualCharges || []).map((charge) => {
      const adviserEmail = normaliseEmail(charge.adviserEmail);
      const linkedAdviser = rateRows.find(
        (row) => normaliseEmail(row.adviserEmail) === adviserEmail,
      );
      const isRecurring = charge.billingType === "recurring";
      const isLoanRepayment = charge.chargeTypeId === "loan-repayment";
      const isAmlCharge = isAmlGuidelineCharge(charge);
      const technologyGuideline = TECHNOLOGY_CHARGES.find(
        (guideline) => guideline.id === charge.chargeTypeId,
      );
      const isTechnologyCharge = Boolean(technologyGuideline);
      const usesTechnologyGuideline = Boolean(
        technologyGuideline &&
          !technologyGuideline.hideInDropdown &&
          Number.isFinite(Number(technologyGuideline.amount)),
      );
      const currentMonthAmount = Number(
        charge.currentMonthlyRepaymentAmount ||
          charge.monthlyRepaymentAmount ||
          charge.amountCharged ||
          0,
      );
      return {
        ...charge,
        adviser:
          charge.adviserName ||
          charge.adviser ||
          linkedAdviser?.adviser ||
          linkedAdviser?.name ||
          adviserEmail,
        adviserEmail,
        manualChargeType: charge.chargeType,
        manualChargeTypeId: charge.chargeTypeId,
        chargeType: isAmlCharge
          ? "AML Checks"
          : isTechnologyCharge
            ? "Technology"
            : "Miscellaneous",
        totalAmountCharged: Number(charge.amountCharged || 0),
        amountCharged: isRecurring || isLoanRepayment
          ? currentMonthAmount
          : Number(charge.amountCharged || 0),
        expectedAmount: usesTechnologyGuideline
            ? Number(technologyGuideline.amount)
            : isRecurring || isLoanRepayment
              ? currentMonthAmount
              : Number(charge.amountCharged || 0),
        expectedGuideline: usesTechnologyGuideline
          ? [technologyGuideline.category, technologyGuideline.description]
              .filter(Boolean)
              .join(" - ")
          : "",
        recurrence: isLoanRepayment
          ? "installment"
          : isRecurring
            ? "monthly"
            : "one_time",
        installments: isLoanRepayment
          ? Number(charge.repaymentInstalments || 1)
          : undefined,
        billingStartDate: isRecurring
          ? charge.chargeFrom || charge.date
          : charge.date,
        fromDate: charge.chargeFrom || charge.date,
        sourcePath: adviserEmail
          ? `${APP_ROUTES.CHARGES_BREAKDOWN}/${encodeURIComponent(adviserEmail)}?month=${selectedMonth}`
          : "",
      };
    });
    return applyAmlPaymentExceptions(
      [...amlRecords, ...paraplanningRecords, ...proCedingRecords, ...rateRecords,
        ...manualChargeRecords,
        ...(sources.websites || []),
        ...(sources.printing || []).map((record) =>
          flagUnlinkedMarketingEmail(record, adviserEmails)),
        ...(sources.email || []).map((record) =>
          flagUnlinkedMarketingEmail(record, adviserEmails))],
      amlPaymentExceptionEmails,
    );
  }, [sources, rateRows, selectedMonth, amlPaymentExceptionEmails]);
  const chargeRecords = useMemo(() => chargesForMonth(loadedChargeRecords, selectedMonth), [loadedChargeRecords, selectedMonth]);
  const selectedMonthClosed = useMemo(
    () => isChargeMonthClosed(incomePeriods || [], selectedMonth),
    [incomePeriods, selectedMonth],
  );
  const advisersByEmail = useMemo(() => {
    const result = {};
    const keyOf = (email) => String(email || "").trim().toLowerCase();
    (sources["Monthly Fees"] || []).forEach((adviser) => {
      const email = keyOf(adviser.adviserEmail);
      if (email) result[email] = { adviser, charges: [], loadedCharges: [] };
    });
    loadedChargeRecords.forEach((charge) => {
      const email = keyOf(charge.adviserEmail);
      if (!email) return;
      if (!result[email]) result[email] = { adviser: null, charges: [], loadedCharges: [] };
      result[email].loadedCharges.push(charge);
      const selectedCharge = chargeRecords.find((record) => record.id === charge.id);
      if (selectedCharge) result[email].charges.push(selectedCharge);
    });
    return result;
  }, [sources, chargeRecords, loadedChargeRecords]);
  return <AdviserChargesContext.Provider value={{ sources, advisersByEmail, loadedChargeRecords, chargeRecords, selectedMonth,
    monthOptions, setSelectedMonth, chargesLoading, chargeErrors,
    incomePeriods, selectedIncomePeriod, setSelectedIncomePeriod, incomePeriodsError,
    selectedMonthClosed }}>{children}</AdviserChargesContext.Provider>;
};
